import { useState } from "react";
import api, { formatApiError, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { CircleNotch } from "@phosphor-icons/react";

const INDICATION_LABELS = { terbukti: "Fraud Terbukti", indikasi_lemah: "Indikasi Lemah", tidak_terbukti: "Fraud Tidak Terbukti" };
const INDICATION_STYLES = {
  terbukti: "bg-red-900 text-red-50 border-red-900",
  indikasi_lemah: "bg-amber-50 text-amber-700 border-amber-300",
  tidak_terbukti: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function ReportSection({ claim, canSubmit, onDone }) {
  const [form, setForm] = useState({ fraud_indication: "", findings: "", summary: "" });
  const [sending, setSending] = useState(false);
  const report = claim.investigation_report;

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/claims/${claim.id}/report`, form);
      toast.success(data.message);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (report) {
    return (
      <div data-testid="investigation-report-view">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Laporan Investigasi</h3>
        <div className="border border-border/60 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span data-testid="report-indication" className={`inline-flex px-2 py-0.5 text-xs font-semibold border ${INDICATION_STYLES[report.fraud_indication]}`}>
              {INDICATION_LABELS[report.fraud_indication]}
            </span>
            <p className="text-xs text-muted-foreground">{report.submitted_by} · {formatDate(report.submitted_at)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Temuan</p>
            <p className="text-sm whitespace-pre-wrap">{report.findings}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Kesimpulan</p>
            <p className="text-sm whitespace-pre-wrap">{report.summary}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!canSubmit) return null;

  return (
    <div className="border border-slate-900 p-4 space-y-3" data-testid="report-form">
      <h3 className="text-sm font-semibold">Laporan Investigasi</h3>
      <div className="space-y-1.5">
        <Label className="text-xs">Hasil Indikasi Fraud</Label>
        <Select value={form.fraud_indication} onValueChange={(v) => setForm((s) => ({ ...s, fraud_indication: v }))}>
          <SelectTrigger data-testid="report-indication-select"><SelectValue placeholder="Pilih hasil" /></SelectTrigger>
          <SelectContent>
            {Object.entries(INDICATION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Temuan Lapangan</Label>
        <Textarea data-testid="report-findings-input" rows={4} value={form.findings}
          onChange={(e) => setForm((s) => ({ ...s, findings: e.target.value }))}
          placeholder="Detail temuan investigasi: hasil wawancara, verifikasi dokumen, kunjungan lapangan..." />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Kesimpulan & Rekomendasi</Label>
        <Textarea data-testid="report-summary-input" rows={3} value={form.summary}
          onChange={(e) => setForm((s) => ({ ...s, summary: e.target.value }))}
          placeholder="Ringkasan kesimpulan dan rekomendasi tindak lanjut klaim" />
      </div>
      <Button data-testid="submit-report-button" disabled={!form.fraud_indication || !form.findings || !form.summary || sending}
        onClick={submit} className="bg-slate-900 hover:bg-slate-800 rounded-none w-full">
        {sending ? <CircleNotch size={16} className="animate-spin" /> : "Kirim Laporan & Tandai Selesai"}
      </Button>
    </div>
  );
}
